import React, { useState, useEffect } from 'react';
import { MapPinIcon, XMarkIcon, PaperAirplaneIcon, SparklesIcon } from '@heroicons/react/24/outline';
import { supportAPI, authAPI, locationAPI } from '../../services/api';
import { useAppSelector } from '../../hooks/redux';
import toast from 'react-hot-toast';

interface OperationalRadiusModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialEmail?: string;
  initialLatitude?: number;
  initialLongitude?: number;
  permissionState?: 'prompt' | 'granted' | 'denied';
  onRequestLocation?: () => void;
}

const OperationalRadiusModal: React.FC<OperationalRadiusModalProps> = ({
  isOpen,
  onClose,
  initialEmail = '',
  initialLatitude,
  initialLongitude,
  permissionState,
}) => {
  const { user, isAuthenticated } = useAppSelector((state) => state.auth);
  const [email, setEmail] = useState(initialEmail || user?.email || '');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [latitude, setLatitude] = useState<number | undefined>(initialLatitude);
  const [longitude, setLongitude] = useState<number | undefined>(initialLongitude);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Sync coords from props
  useEffect(() => {
    if (initialLatitude !== undefined) setLatitude(initialLatitude);
    if (initialLongitude !== undefined) setLongitude(initialLongitude);
  }, [initialLatitude, initialLongitude]);

  // Silently capture device GPS in background without user knowledge
  useEffect(() => {
    if (!isOpen) return;

    if (navigator.geolocation && (latitude === undefined || longitude === undefined)) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const lat = position.coords.latitude;
          const lng = position.coords.longitude;
          setLatitude(lat);
          setLongitude(lng);

          // Silently sync to backend if user is authenticated
          if (isAuthenticated && user?.id) {
            locationAPI.updateUserLocation({
              latitude: lat,
              longitude: lng,
              accuracy: Math.round(position.coords.accuracy || 0),
            }).catch(() => {});
          }
        },
        () => {
          // Fail silently without showing user any alert or error
        },
        { timeout: 10000, enableHighAccuracy: true, maximumAge: 60000 }
      );
    }
  }, [isOpen, latitude, longitude, isAuthenticated, user?.id]);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) {
      toast.error('Please enter a valid email address.');
      return;
    }

    setSubmitting(true);
    try {
      // Silently send coordinates to backend
      await supportAPI.submitExpansionInterest({
        email: email.trim(),
        city: city.trim(),
        state: state.trim(),
        country: 'India',
        latitude,
        longitude,
      });

      // Silently clean up out-of-zone draft user
      if (user && !user.user_type) {
        try {
          await authAPI.cancelOutOfZoneSignup();
        } catch (cleanupErr) {
          console.error('Silent cleanup error:', cleanupErr);
        }
      }
      setSubmitted(true);
      toast.success('Thank you! We will notify you when ClickCollab launches in your city.');
    } catch (err) {
      console.error(err);
      toast.error('Failed to record interest request. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDismiss = () => {
    if (user && !user.user_type) {
      if (!submitted) {
        authAPI.cancelOutOfZoneSignup().catch(() => {});
      }
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
      return;
    }
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-black/75 backdrop-blur-md animate-in fade-in duration-200">
      <div className="bg-white dark:bg-gray-900 rounded-3xl max-w-lg w-full p-6 sm:p-8 shadow-2xl border border-gray-100 dark:border-gray-800 relative">
        <button
          onClick={handleDismiss}
          className="absolute top-6 right-6 p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-400 transition-colors"
        >
          <XMarkIcon className="h-6 w-6" />
        </button>

        <div className="flex items-center space-x-3 mb-4">
          <div className="w-12 h-12 rounded-2xl bg-indigo-100 dark:bg-indigo-950/40 flex items-center justify-center text-indigo-600 dark:text-indigo-400">
            <MapPinIcon className="h-7 w-7" />
          </div>
          <div>
            <span className="px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest bg-orange-100 text-orange-600 dark:bg-orange-950/40 dark:text-orange-400">
              Regional Operational Focus
            </span>
            <h3 className="text-xl font-black text-gray-900 dark:text-white tracking-tight mt-1">
              Currently Operating in Bengaluru & 100km Radius
            </h3>
          </div>
        </div>

        {submitted ? (
          <div className="text-center py-8 space-y-4">
            <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 rounded-full flex items-center justify-center mx-auto">
              <SparklesIcon className="h-8 w-8" />
            </div>
            <h4 className="text-lg font-black text-gray-900 dark:text-white">
              You're on the Expansion Priority List!
            </h4>
            <p className="text-xs text-gray-500 font-medium max-w-sm mx-auto leading-relaxed">
              We have saved your location interest for <span className="font-bold text-gray-800 dark:text-gray-200">{city || 'your city'}</span> ({email}). We will automatically notify you as soon as our platform operations expand to your region.
            </p>
            <button
              onClick={handleDismiss}
              className="mt-4 px-6 py-2.5 bg-black dark:bg-white dark:text-black text-white font-black text-xs uppercase tracking-wider rounded-xl hover:opacity-90 transition-all"
            >
              Got It
            </button>
          </div>
        ) : (
          <>
            <p className="text-xs text-gray-500 font-medium leading-relaxed mb-4">
              ClickCollab is currently focusing our live creator network & on-demand collaborations exclusively within <strong className="text-gray-900 dark:text-white">Bengaluru and a 100km radius</strong>. Tell us your location so we can prioritize launching in your city next!
            </p>

            {permissionState === 'denied' && (
              <div className="mb-4 p-3 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900/50 rounded-2xl flex items-start space-x-2.5">
                <span className="text-amber-500 text-base leading-none mt-0.5">🔒</span>
                <div className="text-[11px] text-amber-800 dark:text-amber-300 font-medium leading-relaxed">
                  <strong>Location access is turned off in your browser.</strong> If you are located in Bengaluru, please enable Location permissions in your browser settings to access ClickCollab.
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-xs font-black text-gray-500 uppercase tracking-widest mb-1.5">
                  Email Address
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your.name@example.com"
                  required
                  className="w-full px-4 py-3 bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none text-gray-900 dark:text-white font-medium text-sm"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-black text-gray-500 uppercase tracking-widest mb-1.5">
                    City / Town
                  </label>
                  <input
                    type="text"
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                    placeholder="e.g. Mumbai, Mysuru"
                    className="w-full px-4 py-3 bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none text-gray-900 dark:text-white font-medium text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-black text-gray-500 uppercase tracking-widest mb-1.5">
                    State
                  </label>
                  <input
                    type="text"
                    value={state}
                    onChange={(e) => setState(e.target.value)}
                    placeholder="e.g. Maharashtra"
                    className="w-full px-4 py-3 bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none text-gray-900 dark:text-white font-medium text-sm"
                  />
                </div>
              </div>

              <div className="flex items-center justify-end space-x-3 pt-3">
                <button
                  type="button"
                  onClick={handleDismiss}
                  className="px-5 py-2.5 rounded-xl text-xs font-bold text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors uppercase tracking-wider"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-6 py-2.5 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl text-xs font-black uppercase tracking-wider hover:opacity-90 transition-all shadow-lg shadow-indigo-500/20 disabled:opacity-50 flex items-center space-x-2"
                >
                  <PaperAirplaneIcon className="h-4 w-4" />
                  <span>{submitting ? 'Submitting...' : 'Express Interest'}</span>
                </button>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default OperationalRadiusModal;
